export type ClerkNavigation =
  | { kind: "spa"; path: string }
  | { kind: "external"; url: string };

/** Where a refused redirect lands: the app root, never the refused URL. */
const BLOCKED_FALLBACK: ClerkNavigation = { kind: "spa", path: "/" };

/**
 * Decide how a Clerk router callback (`routerPush` / `routerReplace`) should
 * navigate.
 *
 * Same-origin destinations stay client-side so Clerk's auth redirects (sign-in,
 * sign-up, post-signup `forceRedirectUrl`) no longer hard-reload the SPA — the
 * hard reload is what made `/pros/welcome` flash the generic shell loader after
 * signup.
 *
 * Cross-origin destinations hard-navigate via `window.location`, because React
 * Router can only move within the app. They do so ONLY when the origin is in
 * `allowedOrigins` (SEC-59): a redirect target can come from a URL parameter
 * (`redirect_url`), so an unlisted origin would be an open redirect, and a
 * `javascript:` URL handed to `location.assign` would run script. Anything
 * refused lands on the app root instead.
 */
export function resolveClerkNavigation(
  to: string,
  origin: string,
  allowedOrigins: readonly string[] = [],
): ClerkNavigation {
  let url: URL;
  try {
    url = new URL(to, origin);
  } catch {
    // Unparseable even against the current origin. Never hand it to the
    // router as-is (it is not a path we can vouch for).
    return BLOCKED_FALLBACK;
  }

  if (url.protocol !== "https:" && url.protocol !== "http:") {
    return BLOCKED_FALLBACK;
  }

  if (url.origin === origin) {
    return { kind: "spa", path: `${url.pathname}${url.search}${url.hash}` };
  }

  if (allowedOrigins.includes(url.origin)) {
    return { kind: "external", url: url.href };
  }
  return BLOCKED_FALLBACK;
}

/** The origin of an absolute URL, or null when it is not one. */
export function originOf(raw: string | undefined): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.origin
      : null;
  } catch {
    return null;
  }
}

/**
 * The Clerk origins a publishable key implies: the Frontend API host encoded
 * in the key, and the Account Portal host Clerk derives from it
 * (`clerk.example.com` → `accounts.example.com` in production,
 * `slug.clerk.accounts.dev` → `slug.accounts.dev` in development).
 */
export function clerkOriginsFromPublishableKey(
  key: string | undefined,
): string[] {
  const encoded = /^pk_(?:test|live)_(.+)$/.exec(key ?? "")?.[1];
  if (!encoded) return [];
  let host: string;
  try {
    host = atob(encoded).replace(/\$$/, "");
  } catch {
    return [];
  }
  if (!/^[a-z0-9.-]+$/i.test(host)) return [];

  const origins = [`https://${host}`];
  if (host.endsWith(".clerk.accounts.dev")) {
    origins.push(
      `https://${host.replace(/\.clerk\.accounts\.dev$/, ".accounts.dev")}`,
    );
  } else if (host.startsWith("clerk.")) {
    origins.push(`https://accounts.${host.slice("clerk.".length)}`);
  }
  return origins;
}
